import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Sparkles, Check, Pause, Play, RotateCcw } from "lucide-react";

interface MuscleRelaxationProps {
  onClose: () => void;
}

const muscleGroups = [
  { id: "feet", name: "Feet & Toes", instruction: "Curl your toes tightly, hold for 5 seconds, then release completely" },
  { id: "calves", name: "Calves", instruction: "Point your toes up toward your shins, hold, then let them fall naturally" },
  { id: "thighs", name: "Thighs", instruction: "Squeeze your thigh muscles tight, hold, then let go" },
  { id: "buttocks", name: "Buttocks", instruction: "Clench your buttocks muscles, hold, then release" },
  { id: "abdomen", name: "Abdomen", instruction: "Pull your belly button toward your spine, hold, then soften" },
  { id: "chest", name: "Chest", instruction: "Take a deep breath in, hold it, then exhale and let your chest relax" },
  { id: "hands", name: "Hands & Fingers", instruction: "Make tight fists, hold, then open your hands and spread your fingers" },
  { id: "arms", name: "Arms", instruction: "Bend your elbows and tense your biceps, hold, then let your arms hang loose" },
  { id: "shoulders", name: "Shoulders", instruction: "Lift your shoulders up to your ears, hold, then let them drop" },
  { id: "neck", name: "Neck", instruction: "Gently tilt your head back, hold briefly, then bring it forward and relax" },
  { id: "face", name: "Face", instruction: "Scrunch your entire face tight, hold, then let everything soften" },
  { id: "jaw", name: "Jaw", instruction: "Clench your teeth, hold, then let your jaw hang loose" },
];

export const MuscleRelaxation = ({ onClose }: MuscleRelaxationProps) => {
  const [currentStep, setCurrentStep] = useState(-1); // -1 = intro
  const [phase, setPhase] = useState<"tense" | "hold" | "release" | "rest">("tense");
  const [countdown, setCountdown] = useState(0);
  const [prepCountdown, setPrepCountdown] = useState(6);
  const [isComplete, setIsComplete] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showReleaseFlash, setShowReleaseFlash] = useState(false);

  useEffect(() => {
    console.log('[PMR] mount');
    return () => console.log('[PMR] unmount');
  }, []);

  useEffect(() => {
    console.log('[PMR] state', { currentStep, phase, isPaused, countdown, prepCountdown });
  }, [currentStep, phase, isPaused, countdown, prepCountdown]);

  useEffect(() => {
    if (currentStep < 0 || isComplete || isPaused) return;

    const phaseDurations = {
      tense: 0, // instruction only
      hold: 10,
      release: 6,
      rest: 6,
    };

    if (phase === "tense") {
      // Show countdown from 3 to 1, then start hold phase
      const interval = setInterval(() => {
        setPrepCountdown((prev) => {
          if (prev <= 1) {
            setPhase("hold");
            setCountdown(phaseDurations.hold);
            return 6; // Reset for next cycle
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (phase === "hold") {
            setShowReleaseFlash(true);
            setTimeout(() => setShowReleaseFlash(false), 1000);
            setPhase("release");
            return phaseDurations.release;
          } else if (phase === "release") {
            setPhase("rest");
            return phaseDurations.rest;
          } else if (phase === "rest") {
            if (currentStep + 1 >= muscleGroups.length) {
              setIsComplete(true);
              return 0;
            }
            setCurrentStep(currentStep + 1);
            setPhase("tense");
            return 0;
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, currentStep, isComplete, isPaused]);

  const startExercise = () => {
    setIsComplete(false);
    setIsPaused(false);
    setPrepCountdown(6);
    setCurrentStep(0);
    setPhase("tense");
  };

  const startTension = () => {
    setPhase("hold");
    setCountdown(5);
    setIsPaused(false);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const reset = () => {
    // Soft reset: keep the active view, restart from the beginning paused on the first muscle group
    setCurrentStep(0);
    setPhase("tense");
    setCountdown(0);
    setPrepCountdown(6);
    setIsPaused(true);
  };

  const progress = currentStep >= 0 ? ((currentStep + 1) / muscleGroups.length) * 100 : 0;

  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-12 text-center relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center">
              <Sparkles className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">Fully Relaxed</h2>
            <p className="text-lg text-muted-foreground mb-2">
              You've released tension from all 12 muscle groups
            </p>
            <p className="text-muted-foreground">
              Your body should feel deeply relaxed and ready for restful sleep.
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button type="button" onClick={reset}>
              Do It Again
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Ready for Sleep
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (currentStep < 0) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="mb-6 gap-2"
          >
            <X className="h-4 w-4" />
            Exit Exercise
          </Button>

          <Card className="p-8 md:p-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4 text-foreground">Progressive Muscle Relaxation</h2>
              <p className="text-lg text-muted-foreground mb-6">
                A proven technique to release physical tension and prepare your body for deep, restorative sleep.
              </p>

              <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold mb-3 text-foreground">How It Works:</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>1. Tense each muscle group for 5 seconds</p>
                  <p>2. Notice the sensation of tension</p>
                  <p>3. Release and feel the relaxation</p>
                  <p>4. Rest for a few seconds before moving on</p>
                  <p className="text-sm mt-4">We'll guide you through 12 muscle groups from feet to face.</p>
                </div>
              </div>

              <Button type="button" size="lg" onClick={startExercise} className="gap-2">
                <Sparkles className="h-5 w-5" />
                Begin Relaxation Exercise
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const currentGroup = muscleGroups[currentStep];

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Exercise
        </Button>

        <Card className="p-8 md:p-12">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Muscle Group {currentStep + 1} of {muscleGroups.length}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Current Muscle Group */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4 text-foreground">{currentGroup.name}</h2>
            <p className="text-lg text-muted-foreground mb-6">
              {currentGroup.instruction}
            </p>

            {phase === "tense" && !isPaused && (
              <div className="flex flex-col items-center">
                <div className="text-6xl font-bold text-primary mb-4">{prepCountdown}</div>
                <p className="text-xl font-semibold text-foreground">Get Ready...</p>
              </div>
            )}

            {phase === "tense" && isPaused && (
              <p className="text-lg text-muted-foreground">Exercise paused. Press Resume to continue.</p>
            )}

            {phase === "hold" && (
              <div className="flex flex-col items-center">
                <div className="text-6xl font-bold text-primary mb-4">{countdown}</div>
                <p className="text-xl font-semibold text-foreground">Hold the Tension</p>
                {countdown <= 2 && (
                  <p className="text-lg text-accent mt-2 animate-pulse">Almost time to release...</p>
                )}
              </div>
            )}

            {phase === "release" && (
              <div className="flex flex-col items-center">
                {showReleaseFlash && (
                  <div className="absolute inset-0 bg-accent/20 animate-fade-in rounded-lg" />
                )}
                <div className="text-6xl font-bold text-accent mb-4">{countdown}</div>
                <p className="text-xl font-semibold text-foreground">Release & Relax</p>
                <p className="text-muted-foreground mt-2">Feel the difference as the tension melts away</p>
              </div>
            )}

            {phase === "rest" && (
              <div className="flex flex-col items-center">
                <Check className="h-16 w-16 text-green-500 mb-4" />
                <p className="text-xl font-semibold text-foreground">Rest & Notice</p>
                <p className="text-muted-foreground mt-2">Feel how relaxed this area has become</p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex gap-3 justify-center mb-6">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={togglePause}
              className="gap-2"
            >
              {isPaused ? (
                <>
                  <Play className="h-5 w-5" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="h-5 w-5" />
                  Pause
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={reset}
              className="gap-2"
            >
              <RotateCcw className="h-5 w-5" />
              Reset
            </Button>
          </div>

          {/* Completed Groups */}
          <div className="flex flex-wrap gap-2 justify-center">
            {muscleGroups.slice(0, currentStep).map((group) => (
              <div key={group.id} className="flex items-center gap-1 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-green-500" />
                <span>{group.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
